import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, Tooltip, Typography, CircularProgress } from '@mui/material';
import { RestartAltRounded as ResetIcon } from '@mui/icons-material';
import { emptyId, useDialogs, usePerformanceReportBuilderPageStore } from '@diana/core';
import { useResetDraftLayoutMutation } from '../Mutations/useResetDraftLayoutMutation';
import { StyledIconButton } from '../PerformanceReportBuilderPage.styles';
import { PERFORMANCE_REPORT_BUILDER_PAGE_NS } from '../i18n';
import { usePerformanceReportBuilderPageContext } from '../PerformanceReportBuilderPageContext';

export const ResetChangesButton = () => {
  const { t } = useTranslation(PERFORMANCE_REPORT_BUILDER_PAGE_NS);
  const { confirm } = useDialogs();

  const { isLoading, error } = usePerformanceReportBuilderPageContext();

  const canReset = usePerformanceReportBuilderPageStore(
    (s) => s.layout && s.layout.originId !== emptyId && s.layout.originId !== s.layout.id,
  );

  const { reloadLayout } = usePerformanceReportBuilderPageContext();
  const { mutate, isPending: isResetting } = useResetDraftLayoutMutation({
    onSuccess: () => reloadLayout(),
  });

  const resetChanges = async () => {
    const title = <Typography>{t('prompts.areYouSureToResetDashboard')}</Typography>;
    const message = t('descriptions.unpublishedChangesWillNotBeSaved');
    const { confirmed } = await confirm(message, {
      size: 'sm',
      title,
      variant: 'warning',
      confirmButtonLabel: t('buttons.resetChanges'),
    });
    if (confirmed) mutate();
  };

  if (isLoading) return <Skeleton variant="circular" width={32} height={32} />;

  if (!error)
    return (
      <Tooltip title={t('tooltips.resetToLastPublishedVersion')}>
        <StyledIconButton
          data-testid="ResetChangesButton"
          disabled={isResetting || !canReset}
          onClick={resetChanges}
        >
          {isResetting ? <CircularProgress size="20px" /> : <ResetIcon fontSize="small" />}
        </StyledIconButton>
      </Tooltip>
    );

  return <Fragment />;
};
